import { useCallback } from 'react';
import {
    useInfiniteQuery,
    useQuery,
    useQueryClient,
    keepPreviousData
} from '@tanstack/react-query';
import {
    fetchPatientsPage,
    fetchPatientFilterOptions,
    type PatientRow,
    type PatientsPageMeta,
    type PatientsParams,
    type PatientFilterOptions
} from '../services/patientsV2';
import { PATIENTS_KEY, PATIENTS_FILTERS_KEY } from './queryKeys';

export function useServerPatients(params: PatientsParams & { enabled?: boolean }) {
    const queryClient = useQueryClient();

    const queryKey = [
        PATIENTS_KEY,
        params.page,
        params.limit,
        params.sort,
        params.order,
        params.practice?.join(',') ?? '',
        params.practiceIds?.join(',') ?? '',
        params.caseStatus?.join(',') ?? '',
        params.macro?.join(',') ?? '',
        params.actionRequired?.join(',') ?? '',
        params.internalStatus?.join(',') ?? '',
        params.search ?? ''
    ];

    const { data, isLoading, isFetching, isError, error } = useQuery({
        queryKey,
        queryFn: () => fetchPatientsPage(params),
        staleTime: 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
        placeholderData: keepPreviousData,
        enabled: params.enabled !== false
    });

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [PATIENTS_KEY] });
    }, [queryClient]);

    return {
        patients: (data?.data ?? []) as PatientRow[],
        meta: (data?.meta ?? {
            total: 0,
            page: 1,
            limit: params.limit,
            totalPages: 1
        }) as PatientsPageMeta,
        isLoading,
        isFetching,
        isError,
        error,
        refresh
    };
}

/**
 * Same endpoint, accumulated instead of sliced - `DataTable`'s `'infinite'`
 * mode. The query key omits `page` on purpose and is nested under
 * `PATIENTS_KEY` so `useServerPatients`'s `refresh()` (which invalidates by
 * that prefix) catches it too, without a second explicit invalidation.
 */
export function useInfinitePatients(params: Omit<PatientsParams, 'page'> & { enabled?: boolean }) {
    const { enabled = true, ...rest } = params;

    const queryKey = [
        PATIENTS_KEY,
        'infinite',
        rest.limit,
        rest.sort,
        rest.order,
        rest.practice?.join(',') ?? '',
        rest.practiceIds?.join(',') ?? '',
        rest.caseStatus?.join(',') ?? '',
        rest.macro?.join(',') ?? '',
        rest.actionRequired?.join(',') ?? '',
        rest.internalStatus?.join(',') ?? '',
        rest.search ?? '',
        rest.practiceId ?? ''
    ];

    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isError,
        error,
        hasNextPage,
        fetchNextPage
    } = useInfiniteQuery({
        queryKey,
        initialPageParam: 1,
        queryFn: ({ pageParam }) => fetchPatientsPage({ ...rest, page: pageParam as number }),
        getNextPageParam: (lastPage) =>
            lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                ? lastPage.meta.page + 1
                : undefined,
        staleTime: 60 * 1000,
        placeholderData: keepPreviousData,
        enabled
    });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        patients: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { total: 0, page: 1, limit: rest.limit, totalPages: 1 },
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
        isError,
        error
    };
}

const EMPTY_FILTERS: PatientFilterOptions = {
    caseStatuses: [],
    actionsRequired: []
};

export function usePatientFilterOptions() {
    const { data, isLoading } = useQuery({
        queryKey: [PATIENTS_FILTERS_KEY],
        queryFn: fetchPatientFilterOptions,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true
    });

    return { filterOptions: data ?? EMPTY_FILTERS, isLoading };
}
